import { createSelector } from '@ngrx/store';
import { createSlice, PayloadAction } from 'ngrx-slice';
import { createSliceEntityAdapter, EntityState } from 'ngrx-slice/entity';

import { ProductImportImages } from '@products/interfaces/product-import-images';
import { ProductImportTableValidations } from '@products/interfaces/product-import-table-validations';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { PageRequest } from '@shared/interfaces/page-request.interface';
import { PageResponse } from '@shared/interfaces/page-response.interface';

export interface ProductImportImagesPageRequest extends PageRequest {}

export interface ProductImportImagesPageSaveRequest {
  formData: FormData;
}

export interface ProductImportTableSaveRequest {
  formData: FormData;
}

export interface DeleteProductImportImageRequest {
  fileNames: string[];
}

export interface ValidateProductImportTableRequest {
  fileName: string;
}

export interface State {
  // products import images - get list
  productImportImagesFilter: ProductImportImagesPageRequest;
  productImportImages: EntityState<ProductImportImages>;
  productImportImagesLoading: boolean;
  productImportImagesLoaded: boolean;
  productImportImagesLoadError: boolean;
  totalProductImportImages: number;
  // products import images - save
  productImportImagesSaving: boolean;
  productImportImagesSaveError: boolean;
  // products import image - delete
  productImportImageDeleting: boolean;
  productImportImagesDeleteError: boolean;
  // products import tables - save
  productImportTablesSaving: boolean;
  productImportTablesSaveError: boolean;

  // products import table - validate
  productImportTableValidations: EntityState<ProductImportTableValidations>;
  productImportTableValidating: boolean;
  productImportTablesValidateError: boolean;
}

export const productImportImagesAdapter =
  createSliceEntityAdapter<ProductImportImages>({
    selectId: (a) => a.name ?? '-1',
  });

export const productImportTableValidationsAdapter =
  createSliceEntityAdapter<ProductImportTableValidations>({
    selectId: (a) =>
      `sheet: ${a.sheet}, col: ${a.column}, row: ${a.rowNumber}, m: ${a.message}` ??
      '-1',
  });

export const initialState: State = {
  // products import images - get list
  productImportImagesFilter: {
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
  },
  productImportImages: productImportImagesAdapter.getInitialState(),
  productImportImagesLoading: false,
  productImportImagesLoaded: false,
  productImportImagesLoadError: false,
  totalProductImportImages: 0,
  // products import images - save
  productImportImagesSaving: false,
  productImportImagesSaveError: false,
  // products import image - delete
  productImportImageDeleting: false,
  productImportImagesDeleteError: false,
  // products import tables - save
  productImportTablesSaving: false,
  productImportTablesSaveError: false,
  // products import table - validate
  productImportTableValidations:
    productImportTableValidationsAdapter.getInitialState(),
  productImportTableValidating: false,
  productImportTablesValidateError: false,
};

export const {
  actions: ProductImportActions,
  selectors,
  ...ProductImportFeature
} = createSlice({
  name: 'product-images',
  initialState,
  reducers: {
    loadProductImportImages: {
      trigger: (
        state,
        action: PayloadAction<{ filter: ProductImportImagesPageRequest }>
      ) => {
        productImportImagesAdapter.setAll(state.productImportImages, []);
        state.productImportImagesFilter = action.filter;
        state.productImportImagesLoading = true;
      },
      success: (
        state,
        action: PayloadAction<PageResponse<ProductImportImages>>
      ) => {
        productImportImagesAdapter.setAll(
          state.productImportImages,
          action.content
        );
        state.totalProductImportImages = action?.totalElements ?? 0;
        state.productImportImagesLoading = false;
        state.productImportImagesLoaded = true;
        state.productImportImagesLoadError = false;
      },
      failure: (state) => ({
        ...state,
        productsLoading: false,
        productsLoadError: true,
      }),
    },
    saveProductImportImages: {
      trigger: (
        state,
        _action: PayloadAction<ProductImportImagesPageSaveRequest>
      ) => {
        state.productImportImagesSaving = true;
      },
      success: (state) => {
        state.productImportImagesSaving = false;
        state.productImportImagesSaveError = false;
      },
      failure: (state) => {
        state.productImportImagesSaving = false;
        state.productImportImagesSaveError = true;
      },
    },
    deleteProductImportImage: {
      trigger: (
        state,
        _action: PayloadAction<DeleteProductImportImageRequest>
      ) => {
        state.productImportImageDeleting = true;
      },
      success: (state) => {
        state.productImportImageDeleting = false;
        state.productImportImagesDeleteError = false;
      },
      failure: (state) => {
        state.productImportImageDeleting = false;
        state.productImportImagesDeleteError = true;
      },
    },
    saveProductImportTables: {
      trigger: (
        state,
        _action: PayloadAction<ProductImportTableSaveRequest>
      ) => {
        state.productImportTablesSaving = true;
      },
      success: (state) => {
        state.productImportTablesSaving = false;
        state.productImportTablesSaveError = false;
      },
      failure: (state) => {
        state.productImportTablesSaving = false;
        state.productImportTablesSaveError = true;
      },
    },
    validateProductImportTables: {
      trigger: (
        state,
        _action: PayloadAction<ValidateProductImportTableRequest>
      ) => {
        state.productImportTableValidating = true;
      },
      success: (
        state,
        action: PayloadAction<ProductImportTableValidations[]>
      ) => {
        try {
          productImportTableValidationsAdapter.setAll(
            state.productImportTableValidations,
            action
          );
        } catch (err) {
          productImportTableValidationsAdapter.setAll(
            state.productImportTableValidations,
            []
          );
        }
        state.productImportTableValidating = false;
        state.productImportTablesValidateError = false;
      },
      failure: (state) => {
        state.productImportTableValidating = false;
        state.productImportTablesValidateError = true;
      },
    },
    reset: (_) => initialState,
  },
});

export const ProductImportSelectors = {
  ...selectors,
  selectProductImportImagesList: createSelector(
    selectors.selectProductImportImages,
    (productsImages) => Object.values(productsImages.entities)
  ),
  selectProductImportTableValidationsList: createSelector(
    selectors.selectProductImportTableValidations,
    (productsTableValidations) =>
      Object.values(productsTableValidations.entities)
  ),
};
